import React, { useMemo, useRef } from 'react'
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import Grid from '@mui/material/Unstable_Grid2'
import { useI18n } from '@ggs/i18n'
import { useModal } from '@ggs/ui/common'
import './StackTable.scss'

/**
 * @typedef {import('../../../definitions').StackTableColumn} StackTableColumn
 * @typedef {import('../../../definitions').StackTableRow} StackTableRow
 */

/**
 * @typedef {Object} StackTableColumns
 * @property {number} lg
 * @property {number} md
 * @property {number} sm
 */
const defaultColumns = {
  lg: 3,
  md: 2,
  sm: 1,
}

/**
 * @typedef {Object} StackTableProps
 * @property {StackTableColumn[]} tableFields
 * @property {StackTableColumns=} columnSizes
 */

/**
 * Stack table that allow to display multiple with label-value columns per row.
 * @param {StackTableProps} props
 * @return {JSX.Element}
 */
const StackTable = ({ tableFields, columnSizes = defaultColumns }) => {
  const modalData = useRef({
    title: '',
    content: '',
  })



  const { modal, triggerModal } = useModal({
    scroll: 'body',
    title: () => (
      <Box className="modal-title">
        <h2>{modalData.current.title}</h2>
      </Box>
    ),
    content: () => {
      return <Box dangerouslySetInnerHTML={{ __html: modalData.current.content }} />
    },
  })

  /**
   * Open table column modal content in a dialog.
   * @param {StackTableColumn} tableColumn
   */
  const openModal = (tableColumn) => {
    modalData.current = {
      title: tableColumn.label,
      content: tableColumn.modalContent || '',
    }

    triggerModal()
  }

  const theme = useTheme()
  const isLarge = useMediaQuery(theme.breakpoints.up('md'))
  const isMid = useMediaQuery(theme.breakpoints.between('sm', 'md'))
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'))

  /**
   * Determine how many columns should be displayed per row.
   * @return {number}
   */
  const determineColumnsPerRow = () => {
    if (isLarge) {
      return columnSizes.lg
    } else if (isMid) {
      return columnSizes.md
    } else if (isSmall) {
      return columnSizes.sm
    } else {
      return 0
    }
  }

  const columnsPerRow = useMemo(() => determineColumnsPerRow(), [isLarge, isMid, isSmall])

  /**
   * Compose table rows with appropriate number of columns based on the screen size cols per row setting.
   * @type {StackTableRow[]}
   */
  const tableData = useMemo(() => {
    /**
     * @type {StackTableRow[]}
     */
    const rows = []

    /**
     * @type {StackTableColumn[]}
     */
    let columns = []
    const totalFields = tableFields.length || 0
    tableFields.forEach((tableField, fieldIndex) => {
      const isLastRow = fieldIndex + 1 === totalFields
      if (columns.length <= columnsPerRow) {
        columns.push(tableField)
      }

      // We reach the max cols per row, start new row.
      if (columns.length === columnsPerRow || isLastRow) {
        rows.push(columns)
        columns = []
      }
    })

    return rows
  }, [isLarge, isMid, isSmall, tableFields])

  const columnSize = 12 / columnsPerRow
  const labelSize = 6
  const valueSize = 6

  /**
   * Compose a stack column item.
   * @param {StackTableColumn} tableColumn
   * @param {number} columnIndex
   * @return {JSX.Element}
   */
  const StackColumn = (tableColumn, columnIndex) => {
    return (
      <Grid key={columnIndex} xs={columnSize} className="stack-table__column">
        <Grid container>
          <Grid xs={labelSize}>
            <Typography component="h5" className="stack-table__title">{tableColumn.label}:</Typography>
          </Grid>
          <Grid xs={valueSize}>
            {tableColumn.modalContent ? (
              <Typography
                onClick={(e) => {
                  e.preventDefault()
                  openModal(tableColumn)
                }}
              >
                <a href={'#'}>{tableColumn.value}</a>
              </Typography>
            ) : (
              <Typography>{tableColumn.value || ''}</Typography>
            )}
          </Grid>
        </Grid>
      </Grid>
    )
  }

  /**
   * Produce stack table row.
   * @param {StackTableRow} rowData
   * @param {number} rowIndex
   * @return {JSX.Element}
   */
  const StackRow = (rowData, rowIndex) => {
    return (
      <Grid key={rowIndex} xs={12} className="stack-table__row">
        <Grid container rowSpacing={2} columnSpacing={2}>
          {rowData.map((columnData, columnIndex) => {
            return StackColumn(columnData, columnIndex)
          })}
        </Grid>
      </Grid>
    )
  }

  return (
    <Grid container className="stack-table">
      {tableData.map((mockRow, rowIndex) => {
        return StackRow(mockRow, rowIndex + 1)
      })}
      {modal}
    </Grid>
  )
}

export default StackTable
